import { Box, Flex, HStack, Text, Tooltip } from '@chakra-ui/react';
import { UserButton, useUser } from '@clerk/nextjs';
import { useMeetingState, useRecording } from '@daily-co/daily-react';
import { ChartingTag } from '@web-app/components/ChartingTag';
import { HeaderInfoBlock } from '@web-app/components/Header/components';
import { MedicareBridgeStatus } from '@web-app/components/MedicareBridge/MedicareBridgeStatus';
import PatientTags from '@web-app/components/PatientTags';
import { useProviderCallContext } from '@web-app/context/ProviderCallContext';
import { UpcomingAppointment } from '@web-app/graphql/healthie/queries/get-upcoming-provider-appointments/types';
import { AppointmentResponse } from '@web-app/hooks/queries/appointment-by-room-name/types';
import { useGetUpcomingAppointments } from '@web-app/hooks/queries/healthie/useGetUpcomingAppointments';
import { useOrganizationLogo } from '@web-app/hooks/whitelabel/useOrganizationLogo';
import { RecordingState } from '@web-app/types/recording-state.enum';
import { sanitizeHealthieDate } from '@web-app/utils/date-time.utils';
import { getAppointmentTags } from '@web-app/utils/patient-tags.utils';
import { calculateBillingMetrics } from '@worker/utils/billable-units.utils';
import { format } from 'date-fns';
import Image from 'next/image';
import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { IoIosMore } from 'react-icons/io';

interface HeaderV2Props {
  appointment?: AppointmentResponse;
}

/**
 * HeaderV2 is a non-fixed version of the Header component that integrates
 * with ProviderCallContext and can be placed directly in a grid layout.
 *
 * Unlike the original Header, this version:
 * - Uses static positioning (not fixed)
 * - No compensator box
 * - Integrates with ProviderCallContext for participant count
 */
export const HeaderV2: FC<HeaderV2Props> = ({ appointment }) => {
  const startTimeRef = useRef(0);
  const totalUnitsRef = useRef<HTMLDivElement>(null);
  const totalTimeRef = useRef<HTMLDivElement>(null);
  const subTextRef = useRef<HTMLDivElement>(null);
  const subText2Ref = useRef<HTMLDivElement>(null);
  const nextUnitRef = useRef<HTMLDivElement>(null);
  const { user } = useUser();
  const logoConfig = useOrganizationLogo();

  // Get state from ProviderCallContext
  const { lifecycle } = useProviderCallContext();
  const { participantCount } = lifecycle;

  // Get Daily.co state
  const meetingState = useMeetingState();
  const recording = useRecording();

  // Map recording state
  const recordingState: RecordingState | undefined = recording.isRecording
    ? RecordingState.Recording
    : participantCount === 1 && meetingState === 'joined-meeting'
      ? RecordingState.Waiting
      : undefined;

  const setUnits = useCallback(() => {
    const elapsedSeconds = Math.floor(
      (Date.now() - startTimeRef.current) / 1000,
    );

    const metrics = calculateBillingMetrics(elapsedSeconds);

    if (totalUnitsRef.current) {
      totalUnitsRef.current.innerText = metrics.billableUnits.toString();
    }

    if (totalTimeRef.current) {
      totalTimeRef.current.innerText = metrics.totalMinutes.toString();
    }

    if (nextUnitRef.current) {
      nextUnitRef.current.innerText = metrics.formattedNextUnitTime;
    }

    if (subTextRef.current) {
      subTextRef.current.innerText = metrics.minutesUnitLabel;
    }

    if (subText2Ref.current) {
      subText2Ref.current.innerText = metrics.unitsLabel;
    }

    // Schedule next update
    setTimeout(setUnits, 1000);
  }, []);

  useEffect(() => {
    if (participantCount < 2 || startTimeRef.current) return;

    startTimeRef.current = Date.now();

    setUnits();
  }, [participantCount, setUnits]);

  const chartingNoteEnabled = !!user;
  const isProviderInCall =
    chartingNoteEnabled && meetingState === 'joined-meeting';

  const { data: appointmentsData, isPending: isLoadingUpcomingAppointments } =
    useGetUpcomingAppointments();

  const nextAppointment = useMemo(() => {
    if (
      !appointmentsData?.upcomingAppointments ||
      !appointmentsData.upcomingAppointments.length ||
      !appointment?.healthie?.id
    ) {
      return null;
    }
    const currentAppointmentIndex =
      appointmentsData.upcomingAppointments.findIndex(
        (a: UpcomingAppointment) => a.id === appointment?.healthie?.id,
      );

    const nextAppointment = appointmentsData.upcomingAppointments.find(
      (a: UpcomingAppointment, idx: number) => {
        if (idx <= currentAppointmentIndex) return false;

        const currentAppointmentDate = sanitizeHealthieDate(
          appointment?.healthie?.date,
        );
        const nextAppointmentDate = sanitizeHealthieDate(a.date);

        const isSameDay =
          format(new Date(), 'M/d/yyyy') ===
          format(nextAppointmentDate, 'M/d/yyyy');

        const isInFuture = currentAppointmentDate < nextAppointmentDate;
        return isSameDay && isInFuture;
      },
    );
    return nextAppointment;
  }, [appointment?.healthie, appointmentsData?.upcomingAppointments]);

  return (
    <HStack
      as="header"
      justifyContent={user ? 'space-between' : 'center'}
      alignItems="center"
      w="100%"
      h="56px"
      px="24px"
      py="9px"
      background={user ? '#1B1F26' : 'transparent'}
      position="relative"
    >
      <HStack gap={8}>
        <Flex justifyContent="center" alignItems="center">
          {logoConfig && (
            <Image
              src={logoConfig.getLogoSrc({
                logoColor: 'white',
                variant:
                  meetingState === 'joined-meeting' ? 'light' : 'default',
              })}
              alt={logoConfig.altText}
              width={!user ? 160 : 128}
              height={!user ? 40 : 32}
              style={{
                objectFit: 'contain',
                filter: logoConfig.whiteLogoFilter,
              }}
            />
          )}
        </Flex>

        {isProviderInCall && (
          <>
            {!!appointment?.healthie?.appointment_type && (
              <HeaderInfoBlock
                label="Appointment type"
                text={appointment?.healthie?.appointment_type?.name}
              />
            )}
            <PatientTags tags={getAppointmentTags(appointment)} />
            {appointment?.client_id != null && (
              <MedicareBridgeStatus
                patientHealthieId={appointment.client_id.toString()}
                onDark
              />
            )}
            {!isLoadingUpcomingAppointments && (
              <HeaderInfoBlock
                label="Next session"
                text={
                  nextAppointment
                    ? format(
                        sanitizeHealthieDate(nextAppointment.date),
                        'h:mm a',
                      )
                    : "You're done for today!"
                }
              />
            )}
          </>
        )}
      </HStack>

      {chartingNoteEnabled && (
        <Box
          position="absolute"
          top="30px"
          left="50%"
          transform="translate(-50%, -50%)"
        >
          {meetingState === 'joined-meeting' &&
            recordingState === RecordingState.Waiting && (
              <Tooltip
                hasArrow
                arrowSize={10}
                offset={[0, 20]}
                background="darkBg.100"
                borderRadius={8}
                border="1px solid border.subtle"
                boxShadow="0px 4px 16px 0px #00000052"
                p={4}
                label={
                  <>
                    <Text
                      fontWeight="600"
                      fontSize="16px"
                      lineHeight="24px"
                      pb={2}
                    >
                      Recording waiting to start
                    </Text>
                    <Text fontSize="14px" lineHeight="24px" opacity={0.8}>
                      The recording with start automatically when the patient
                      joins the session.
                    </Text>
                  </>
                }
              >
                <Box
                  fontSize="12px"
                  color="#ABABAB"
                  fontWeight="500"
                  display="flex"
                  gap="4px"
                  alignItems="center"
                >
                  <IoIosMore size="22px" fill="#ABABAB" /> Waiting to start
                  charting
                </Box>
              </Tooltip>
            )}

          {recordingState === RecordingState.Recording && <ChartingTag />}
        </Box>
      )}

      <HStack gap={8}>
        {isProviderInCall && participantCount > 1 && (
          <>
            <HeaderInfoBlock
              label="Time passed"
              text="0"
              subText="min"
              text2="0"
              subText2="unit"
              textRef={totalTimeRef}
              subTextRef={subTextRef}
              text2Ref={totalUnitsRef}
              subText2Ref={subText2Ref}
              tooltip2="Billable units are 15-minute increments used for insurance reimbursement. Sessions lasting at least 8 minutes into the next unit round up, while those under 7 minutes round down."
            />

            <HeaderInfoBlock
              label="Next unit"
              text="15:00"
              textRef={nextUnitRef}
              tooltip="Time remaining until your next billable unit unlocks! We provide this so you can easily pace your appointments and maximize billing."
            />
          </>
        )}

        {user && <UserButton />}
      </HStack>
    </HStack>
  );
};
